import { stableStringify } from '@provision/formatting-utils';
import { useQuery } from '@provision/graphql-client';
import { JSONContent } from '@provision/rich-text-editor';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { ConversationMessageDraft } from 'src/graphql';
import { GET_CONVERSATION_MESSAGE_DRAFT_QUERY } from 'src/graphql/entities/conversation-message-draft/conversation-message-draft.operations';
import { Records } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { ConversationMessageDraftRaw } from 'src/types';
import { parseJsonSafely } from 'src/utils/objects';
import { htmlToPlainText } from 'src/utils/text-parsing';

export interface GetUserConversationMessageDraft {
  getUserConversationMessageDraft: Records<ConversationMessageDraftRaw> & {
    __typename?: 'GetUserConversationMessageDraft';
  };
}

const VISIBILITY_LIMIT_DAYS = 10;
const MS_IN_DAY = 24 * 60 * 60 * 1000;

export const useConversationMessageDraft = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { data, loading } = useQuery<GetUserConversationMessageDraft>(
    GET_CONVERSATION_MESSAGE_DRAFT_QUERY,
    {
      context: {
        omitIsolationModeHeader: true,
      },
    },
  );

  const { allDrafts, visibleDrafts } = useMemo(() => {
    const allDrafts = (data?.getUserConversationMessageDraft.records ?? []).map((i) =>
      mapDraft(i),
    );
    const cutoff = Date.now() - VISIBILITY_LIMIT_DAYS * MS_IN_DAY;

    const visibleDrafts = allDrafts
      .filter((d) => {
        const ts = new Date(d.updatedAt ?? d.createdAt).getTime();
        const matchesPartner =
          isNullish(selectedPartnerId) || d.partnerId === selectedPartnerId;

        const hasSchedule =
          !!d.scheduledAt || !!d.sideEffects?.actions?.length || !!d.sendUnless?.length;

        return (
          matchesPartner &&
          Number.isFinite(ts) &&
          ts >= cutoff &&
          (isDraftNonEmpty(d.message) || hasSchedule)
        );
      })
      .toSorted((a, b) =>
        new Date(a.updatedAt ?? a.createdAt).getTime() >
        new Date(b.updatedAt ?? b.createdAt).getTime()
          ? -1
          : 1,
      );

    return { allDrafts, visibleDrafts };
  }, [data?.getUserConversationMessageDraft.records, selectedPartnerId]);

  // `allDrafts` is the unfiltered set (no partner/age/empty filtering) and is the
  // authoritative source for whether a draft *exists* for a given scope; the
  // `visibleDrafts`/`drafts` views drive the My Drafts list and navbar count.
  return { drafts: visibleDrafts, visibleDrafts, allDrafts, isLoading: loading };
};

export function isDraftNonEmpty(message?: string | null): boolean {
  if (!message) return false;

  try {
    const doc = JSON.parse(message) as JSONContent;
    if (doc && typeof doc === 'object') {
      return draftHasText(doc);
    }
  } catch {
    // legacy HTML fallback
    return htmlToPlainText(message).length > 0;
  }

  return false;
}

function draftHasText(node: JSONContent): boolean {
  if (!node || typeof node !== 'object') return false;

  // text node with non-whitespace
  if (node.type === 'text' && typeof node.text === 'string' && /\S/.test(node.text)) {
    return true;
  }

  // descend
  const children = Array.isArray(node.content) ? node.content : [];
  for (const item of children) {
    if (draftHasText(item)) return true;
  }

  return false;
}

export function mapDraft(draft: ConversationMessageDraftRaw): ConversationMessageDraft {
  const sideEffectsStr = draft.sideEffects;
  const sendUnlessStr = Array.isArray(draft.sendUnless)
    ? stableStringify(draft.sendUnless)
    : draft.sendUnless;

  return {
    ...draft,
    isScheduled: Boolean(draft.scheduledAt),
    sideEffects:
      sideEffectsStr && sideEffectsStr !== '{}'
        ? parseJsonSafely<ConversationMessageDraft['sideEffects']>(sideEffectsStr)
        : null,
    sendUnless:
      sendUnlessStr && sendUnlessStr !== '[]'
        ? parseJsonSafely<ConversationMessageDraft['sendUnless']>(sendUnlessStr)
        : null,
    discardedAt: draft.discardedAt ?? null,
  };
}
